
import { MinutePackage } from "@/types";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Crown } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "@/lib/currency";

interface MinutePackageCardProps {
  packageData: MinutePackage;
  basePricePerMinute?: number;
  onSelect: (packageData: MinutePackage) => void;
}

const MinutePackageCard = ({ packageData, basePricePerMinute, onSelect }: MinutePackageCardProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleSelect = () => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      onSelect(packageData);
      setIsLoading(false);
    }, 500);
  };

  const getIcon = () => {
    const key = packageData.titleKey || '';
    if (key === 'starter') return <Zap className="h-5 w-5 text-blue-500" />;
    if (key === 'professional') return <Crown className="h-5 w-5 text-purple-500" />;
    return <Sparkles className="h-5 w-5 text-brand-500" />;
  };

  const currencySymbol = getCurrencySymbol(packageData.currency);
  const pricePerMinute = packageData.price / packageData.minutes;
  const currency = packageData.currency || 'RUB';
  const decimals = currency !== 'RUB' && pricePerMinute < 0.10 ? 3 : 2;
  const formattedPricePerMinute = pricePerMinute.toFixed(decimals);
  const savingsPercent = basePricePerMinute && basePricePerMinute > pricePerMinute
    ? Math.round(((basePricePerMinute - pricePerMinute) / basePricePerMinute) * 100)
    : 0;
  const displayName = packageData.titleKey
    ? t(`balance.package_name.${packageData.titleKey}`, { defaultValue: packageData.name })
    : packageData.name;

  return (
    <div className={cn(
      "relative flex flex-col rounded-2xl border bg-card p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg",
      packageData.isPopular
        ? "border-brand-500/50 shadow-md shadow-brand-500/10 ring-1 ring-brand-500/20"
        : "border-border/60 hover:border-border"
    )}>
      {packageData.isPopular && (
        <div className="absolute -top-3 left-0 right-0 mx-auto w-fit rounded-full bg-gradient-to-r from-brand-600 to-brand-500 px-3 py-1 text-xs font-medium text-white shadow-sm">
          {t('balance.package.popular')}
        </div>
      )}

      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          {getIcon()}
          <span className="text-sm font-medium uppercase tracking-wider">{displayName}</span>
        </div>
      </div>

      <div className="mb-2 flex items-baseline gap-1">
        <span className="text-4xl font-bold tracking-tight text-foreground">
          {packageData.minutes}
        </span>
        <span className="text-xl font-medium text-muted-foreground">{t('balance.package.minutes')}</span>
      </div>

      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-brand-600">
          {packageData.price} {currencySymbol}
        </span>
        <span className="text-sm text-muted-foreground/80">
          ({formattedPricePerMinute} {currencySymbol} {t('balance.package.price_per_min')})
        </span>
      </div>

      {savingsPercent > 0 && (
        <div className="mb-6">
          <span className="inline-block rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
            {t('balance.package.savings', { percent: savingsPercent })}
          </span>
        </div>
      )}

      {savingsPercent === 0 && <div className="mb-6" />}

      <Button
        className={cn(
          "w-full font-semibold transition-all mt-auto",
          packageData.isPopular
            ? "bg-brand-600 hover:bg-brand-700 text-white shadow-md hover:shadow-lg"
            : "bg-brand-100 text-brand-700 hover:bg-brand-200"
        )}
        onClick={handleSelect}
        disabled={isLoading}
        size="lg"
      >
        {isLoading ? t('balance.package.processing') : t('balance.package.buy_now')}
      </Button>
    </div>
  );
};

export default MinutePackageCard;
